import { Bot, Coins, MessageCircleReply, MessagesSquare, Plus, Wallet } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { ChartFrame } from "../../charts/ChartFrame";
import { SERIES } from "../../charts/palette";
import { TimeChart } from "../../charts/TimeChart";
import { fmt, isAddon, PRODUCT_LABEL, productCode, when } from "../../lib/format";
import { useMe, useOverview } from "../../lib/queries";
import type { OverviewProject } from "../../lib/types";
import { Badge, LevelBadge, StatusBadge } from "../../ui/Badge";
import { Button } from "../../ui/Button";
import { Card } from "../../ui/Card";
import { ErrorState, PageSkeleton } from "../../ui/Feedback";
import { Meter, PageHeader, Segmented } from "../../ui/Misc";
import { StatTile } from "../../ui/StatTile";
import table from "../../ui/Table.module.css";
import { NewProjectDialog } from "./NewProjectDialog";
import styles from "./OverviewPage.module.css";

const RANGES = [
  { value: 7, label: "7 hari" },
  { value: 30, label: "30 hari" },
  { value: 90, label: "90 hari" },
];

export function OverviewPage() {
  const [days, setDays] = useState(30);
  const [creating, setCreating] = useState(false);
  const overview = useOverview(days);
  const me = useMe().data!;
  const navigate = useNavigate();

  if (overview.isPending) return <PageSkeleton />;
  if (overview.isError) return <ErrorState error={overview.error} />;
  const { totals, series } = overview.data;
  // Archived projects live in the sidebar's archive, not in the working portfolio.
  const projects = overview.data.projects.filter((project) => !project.archivedAt);
  const needsAttention = [...projects]
    .filter((project) => project.status === "active" && project.level !== "ok")
    .sort((a, b) => (a.daysLeft ?? 0) - (b.daysLeft ?? 0));

  return (
    <div className={styles.page} style={{ opacity: overview.isPlaceholderData ? 0.6 : 1 }}>
      <PageHeader
        kicker="Portofolio"
        title="Semua chatbot klien"
        description={`${totals.activeProjects} chatbot aktif dari ${totals.projects} proyek · ${when.day(overview.data.range.from)} – ${when.day(overview.data.range.to)}`}
        actions={
          <>
            <Segmented label="Rentang waktu" value={days} options={RANGES} onChange={setDays} />
            {me.role === "admin" && (
              <Button variant="primary" icon={<Plus aria-hidden />} onClick={() => setCreating(true)}>
                Proyek baru
              </Button>
            )}
          </>
        }
      />

      <div className={styles.tiles}>
        <StatTile
          emphasis
          label="Saldo klien yang dititipkan"
          value={fmt.rupiah(totals.saldoHeldMilli)}
          sub={`Dana titipan, bukan pendapatan. Pendapatan dari pemakaian (faktor harga saldo) periode ini: ${fmt.rupiah(totals.usageMarginMilli)}.`}
          icon={<Wallet aria-hidden />}
        />
        <StatTile label="Percakapan" value={fmt.int(totals.conversations)} sub={`${fmt.int(totals.messages)} jawaban AI`} icon={<MessagesSquare aria-hidden />} trend={series.map((d) => d.conversations)} />
        <StatTile label="Token diproses" value={fmt.compact(totals.tokens)} sub="input + output + cache" icon={<Bot aria-hidden />} trend={series.map((d) => d.tokens)} />
        <StatTile
          label="Biaya penyedia AI"
          value={fmt.rupiah(totals.providerCostMilli)}
          sub={totals.navibizCostMilli > 0 ? `${fmt.rupiah(totals.navibizCostMilli)} ditanggung Navibiz (uji & playground)` : "seluruhnya ditutup saldo klien"}
          icon={<Coins aria-hidden />}
          trend={series.map((d) => d.providerCostMilli)}
        />
        <StatTile
          label="Dialihkan ke WhatsApp"
          value={fmt.int(projects.reduce((sum, project) => sum + project.handoffs, 0))}
          sub="pengunjung yang diteruskan ke tim klien"
          icon={<MessageCircleReply aria-hidden />}
        />
      </div>

      <div className={styles.charts}>
        <Card title="Percakapan per hari" description="Semua klien, kanal widget dan API">
          <ChartFrame table={{ columns: ["Tanggal", "Percakapan", "Jawaban AI"], rows: series.map((d) => [when.day(d.day), fmt.int(d.conversations), fmt.int(d.messages)]) }}>
            <TimeChart kind="area" data={series} series={[{ key: "conversations", label: "Percakapan", color: SERIES.blue }]} format={fmt.compact} ariaLabel="Percakapan per hari" />
          </ChartFrame>
        </Card>
        <Card title="Biaya penyedia per hari" description="Token × harga resmi × kurs × faktor biaya 1,15. Saldo klien dipotong dengan faktor 1,5.">
          <ChartFrame table={{ columns: ["Tanggal", "Biaya penyedia", "Dipotong dari saldo"], rows: series.map((d) => [when.day(d.day), fmt.rupiah(d.providerCostMilli), fmt.rupiah(d.costMilli)]) }}>
            <TimeChart
              kind="area"
              data={series.map((d) => ({ ...d, rupiah: d.providerCostMilli / 1000 }))}
              series={[{ key: "rupiah", label: "Biaya penyedia", color: SERIES.blue }]}
              format={(value) => `Rp ${fmt.compact(value)}`}
              ariaLabel="Biaya penyedia per hari"
            />
          </ChartFrame>
        </Card>
      </div>

      {needsAttention.length > 0 && (
        <Card title="Perlu top-up" description="Klien aktif dengan saldo di bawah 30% dari top-up terakhir — hubungi PIC sebelum chatbot berhenti.">
          <ul className={styles.attention}>
            {needsAttention.map((project) => (
              <li key={project.id}>
                <button type="button" onClick={() => navigate(`/projects/${project.id}/saldo`)}>
                  <span className={styles.attentionName}>{project.name}</span>
                  <LevelBadge level={project.level} />
                  <span className={styles.attentionMeta}>
                    {fmt.rupiah(project.balanceMilli)}
                    {project.daysLeft !== null && ` · ±${project.daysLeft} hari lagi`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card title="Proyek" description="Klik baris untuk membuka proyek." flush>
        <ProjectsTable projects={projects} onOpen={(id) => navigate(`/projects/${id}`)} />
      </Card>

      <NewProjectDialog open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

function ProjectsTable({ projects, onOpen }: { projects: OverviewProject[]; onOpen: (id: string) => void }) {
  return (
    <div className={table.wrap}>
      <table className={table.table}>
        <thead>
          <tr>
            <th className={table.first}>Klien</th>
            <th>Produk</th>
            <th>Status</th>
            <th>Saldo</th>
            <th className={table.right}>Percakapan</th>
            <th className={table.right}>Token</th>
            <th className={`${table.right} ${table.last}`}>Terpotong</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((project) => {
            const ratio = project.lastTopupMilli ? project.balanceMilli / project.lastTopupMilli : 0;
            return (
              <tr
                key={project.id}
                className={table.clickable}
                tabIndex={0}
                onClick={() => onOpen(project.id)}
                onKeyDown={(event) => event.key === "Enter" && onOpen(project.id)}
              >
                <td className={table.first}>
                  <div className={styles.client}>{project.name}</div>
                  <div className={table.muted}>{project.clientName}</div>
                </td>
                <td>
                  <Badge tone={isAddon(project.product) ? "accent" : "neutral"}>
                    {project.packageCode ? `${project.packageCode} + ${productCode(project.product)}` : PRODUCT_LABEL[project.product]}
                  </Badge>
                </td>
                <td>
                  <StatusBadge status={project.status} />
                </td>
                <td className={styles.saldoCell}>
                  <div className={styles.saldoTop}>
                    <span className="num">{fmt.rupiah(project.balanceMilli)}</span>
                    <LevelBadge level={project.level} />
                  </div>
                  <Meter ratio={ratio} level={project.level} label={`Saldo ${project.name}`} />
                  <div className={table.muted}>{project.daysLeft === null ? "belum ada pemakaian" : project.daysLeft === 0 ? "chatbot berhenti" : `±${project.daysLeft} hari lagi`}</div>
                </td>
                <td className={table.right}>{fmt.int(project.conversations)}</td>
                <td className={table.right}>{fmt.tokens(project.tokens)}</td>
                <td className={`${table.right} ${table.last}`}>{fmt.rupiah(project.costMilli)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

